import type { DirectiveBinding, ObjectDirective } from 'vue'

type PressHandler = (event: PointerEvent) => void

export interface PressOptions {
	handler: PressHandler
	interval?: number
	delay?: number
	immediate?: boolean
}

interface PressElement extends HTMLElement {
	_pressState?: {
		options: PressOptions | null
		timer: ReturnType<typeof setInterval> | null
		delayTimer: ReturnType<typeof setTimeout> | null
		pointerId: number | null
		start: (event: PointerEvent) => void
		stop: (event?: PointerEvent) => void
	}
}

function getOptions(binding: DirectiveBinding<PressHandler | PressOptions>): PressOptions | null {
	if (typeof binding.value === 'function') {
		return {
			handler: binding.value,
			interval: 100,
			delay: 0,
			immediate: true
		}
	}

	if (binding.value && typeof binding.value.handler === 'function') {
		return {
			interval: 100,
			delay: 0,
			immediate: true,
			...binding.value
		}
	}

	console.warn('[v-press] Binding value must be a function or an object with handler')
	return null
}

function clearPress(state: NonNullable<PressElement['_pressState']>) {
	if (state.timer) {
		clearInterval(state.timer)
		state.timer = null
	}
	if (state.delayTimer) {
		clearTimeout(state.delayTimer)
		state.delayTimer = null
	}
	state.pointerId = null
}

function createState(el: PressElement, binding: DirectiveBinding<PressHandler | PressOptions>) {
	const state: NonNullable<PressElement['_pressState']> = {
		options: getOptions(binding),
		timer: null,
		delayTimer: null,
		pointerId: null,
		start: event => {
			if (!state.options || state.pointerId !== null) return
			state.pointerId = event.pointerId
			el.setPointerCapture?.(event.pointerId)

			const run = () => {
				state.options?.handler(event)
			}

			if (state.options.immediate !== false) {
				run()
			}

			const startTimer = () => {
				state.timer = setInterval(run, Number(state.options?.interval) || 100)
			}

			if (state.options.delay && state.options.delay > 0) {
				state.delayTimer = setTimeout(startTimer, state.options.delay)
			} else {
				startTimer()
			}
		},
		stop: event => {
			if (event && state.pointerId !== null && event.pointerId !== state.pointerId) return
			clearPress(state)
		}
	}

	return state
}

export const vPress: ObjectDirective<PressElement, PressHandler | PressOptions> = {
	mounted(el, binding) {
		const state = createState(el, binding)
		el._pressState = state
		el.addEventListener('pointerdown', state.start)
		el.addEventListener('pointerup', state.stop)
		el.addEventListener('pointerleave', state.stop)
		el.addEventListener('pointercancel', state.stop)
		el.addEventListener('lostpointercapture', state.stop)
	},
	updated(el, binding) {
		const options = getOptions(binding)
		if (el._pressState) {
			el._pressState.options = options
		}
	},
	unmounted(el) {
		const state = el._pressState
		if (!state) return
		clearPress(state)
		el.removeEventListener('pointerdown', state.start)
		el.removeEventListener('pointerup', state.stop)
		el.removeEventListener('pointerleave', state.stop)
		el.removeEventListener('pointercancel', state.stop)
		el.removeEventListener('lostpointercapture', state.stop)
		delete el._pressState
	}
}
